import { useEffect, useRef, useState } from "react";
import { getPerf, onPerf } from "../../lib/perf";

interface LivingSubstrateProps {
  className?: string;
}

export function LivingSubstrate({ className }: LivingSubstrateProps): JSX.Element {
  const grainRef = useRef<HTMLCanvasElement | null>(null);
  const [reduced, setReduced] = useState(getPerf().reducedMotion);

  useEffect(() => {
    const canvas = grainRef.current;
    if (!canvas) return undefined;

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(2, Math.floor((rect.width || 1) * dpr));
      const h = Math.max(2, Math.floor((rect.height || 1) * dpr));
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const image = ctx.createImageData(w, h);
      for (let i = 0; i < image.data.length; i += 4) {
        const v = 150 + Math.random() * 75;
        image.data[i] = v;
        image.data[i + 1] = v;
        image.data[i + 2] = v;
        image.data[i + 3] = 18;
      }
      ctx.putImageData(image, 0, 0);
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    const unsubscribe = onPerf((perf) => {
      setReduced(perf.reducedMotion);
      if (perf.tier !== "low") draw();
    });
    return () => {
      ro.disconnect();
      unsubscribe();
    };
  }, []);

  return (
    <div className={className} aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      <div
        className={reduced ? undefined : "living-substrate-flow"}
        style={{
          position: "absolute",
          inset: "-18%",
          background: [
            "radial-gradient(ellipse 55% 46% at 18% 28%, rgba(214, 190, 122, 0.18), transparent 62%)",
            "radial-gradient(ellipse 58% 52% at 82% 76%, rgba(184, 56, 86, 0.42), transparent 66%)",
            "linear-gradient(135deg, #8C1F2F 0%, #5A1422 42%, #A02C44 68%, #251018 100%)",
          ].join(", "),
          transform: "scale(1.04)",
          filter: "saturate(1.04)",
        }}
      />
      <canvas
        ref={grainRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          mixBlendMode: "overlay",
          opacity: 0.18,
        }}
      />
    </div>
  );
}

export default LivingSubstrate;
